import React, { useEffect } from 'react';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { Link, useLoaderData } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
//todo: exclude from prod build
import PropTypes from 'prop-types';
import { CONFIG_SHAPE } from '../../state/config/config';

import Footer from '../Footer/Footer';
import NewsletterSection from '../NewsletterSection/NewsletterSection';
import RecentlyViewedProducts from '../RecentlyViewedProducts/RecentlyViewedProducts';
import StickyHeader from '../StickyHeader/StickyHeader';

import { ReactComponent as CalendarIcon } from '../../images/icons/calendar.svg';
import { ReactComponent as ClockIcon } from '../../images/icons/time.svg';
import { ReactComponent as ArrowLeftIcon } from '../../images/icons/arrowLeft.svg';
import { ReactComponent as ArrowRightIcon } from '../../images/icons/arrowRight.svg';
import { ReactComponent as HomeIcon } from '../../images/icons/home.svg';

import styles from './BlogPage.module.scss';
import urlcat from 'urlcat';
import SimilarShops from '../SimilarShops/SimilarShops';
import RelatedProductsList from '../RelatedProductsList/RelatedProductsList';
import { clipTextEllipsis } from '../../utils/textEllipsis';

const BlogPage = ({ config }) => {
  const { data, allPostsData } = useLoaderData();

  useEffect(() => {
    window._TradeTrackerTagOptions = {
      t: process.env.REACT_APP_TRADETRACKER_SUPERTAG_TAG_TYPE,
      s: process.env.REACT_APP_TRADETRACKER_SUPERTAG_TAG_SHOP,
      chk: process.env.REACT_APP_TRADETRACKER_SUPERTAG_TAG_SECRET,
      overrideOptions: {
        cookieDirective_disable: true,
      },
    };

    const script = document.createElement('script');
    const protocol = document.location.protocol === 'https:' ? 'https' : 'http';

    script.src =
      protocol +
      '://tm.tradetracker.net/tag?t=' +
      window._TradeTrackerTagOptions.t +
      '&amp;s=' +
      window._TradeTrackerTagOptions.s +
      '&amp;chk=' +
      window._TradeTrackerTagOptions.chk;
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const getBlogsWithSlugs = () => {
    const items = [...allPostsData];
    return items.filter((blog) => !!blog.slug);
  };

  const getCurrentIndex = () => {
    return allPostsData.findIndex((blog) => blog?.slug === data?.slug);
  };

  const getNextBlog = () => {
    const blogs = getBlogsWithSlugs();
    const index = getCurrentIndex(blogs);
    const nextIndex = index + 1;

    if (nextIndex <= blogs.length - 1) {
      return blogs[nextIndex] ? blogs[nextIndex] : null;
    }

    return null;
  };

  const getPreviousBlog = () => {
    const blogs = getBlogsWithSlugs();
    const index = getCurrentIndex(blogs);
    const previousIndex = index - 1;

    if (previousIndex >= 0) {
      return blogs[previousIndex] ? blogs[previousIndex] : null;
    }

    return null;
  };

  const previousBlog = getPreviousBlog();
  const nextBlog = getNextBlog();
  const images = [];

  const blogContent = parse(data.post_content, {
    replace: (domNode) => {
      if (domNode.name === 'img') {
        const imgSrc = domNode.attribs.src;

        images.push(domNode.attribs.src);
        // Cloudinary removed - using direct image
        // Previous sizes: default w_391,h_207; srcset: 1560w(w_1080,h_572), 1200w(w_810,h_429), 984w(w_648,h_343), 768w(w_739,h_392), 576w(w_546,h_289)
        domNode.attribs.src = imgSrc;

        domNode.attribs.sizes = `
          (min-width: 1560px) width: 1080px,
          (min-width: 1200w) width: 810px,
          (min-width: 984w) width: 648px,
          (min-width: 768w) width: 739px,
          (min-width: 576w) width: 546px,
          width: 391px
        `
          .replace(/[\n\s]+/g, ' ')
          .trim();

        domNode.attribs.width = domNode.attribs.width || '391';
        domNode.attribs.height = domNode.attribs.height || '207';

        domNode.attribs.className = domNode.attribs.class;
        delete domNode.attribs.class;

        return (
          <img
            {...domNode.attribs}
            alt={domNode.attribs.alt || `Image from ${data.title}`}
            loading="lazy"
            title={domNode.attribs.alt || `Image from ${data.title}`}
          />
        );
      }
    },
  });
  //todo: well... would be nice to rename this function
  const scrollToShop = (item) => {
    const elm = document.getElementById(item);
    window.scrollTo({ behavior: 'smooth', left: 0, top: elm.offsetTop - 75 });
  };

  const datePublished = new Date(data.date);
  datePublished.setMinutes(+(datePublished.getTimezoneOffset() * -1));

  return (
    <>
      <Helmet>
        <title>{clipTextEllipsis(data?.post_meta?.yoast_wpseo_title || data.title, 60)}</title>
        <meta property="og:url" content={urlcat(config.baseUrl, data.slug)} />
        <meta name="description" content={data?.post_meta?.yoast_wpseo_metadesc || data.title} />
        <link rel="canonical" href={urlcat(config.baseUrl, data.slug)} />
      </Helmet>
      <StickyHeader />
      <div className={classNames('container')} data-testid="blogDetailContainer" id="readyForCrawler">
        <div className={classNames('row')}>
          <div className={classNames(styles.breadCrumbs, 'col-12-m')}>
            <Link to="/">
              <HomeIcon /> <FormattedMessage id="common.home" defaultMessage="Home" />
            </Link>
            <Link to={data.slug}>
              <ArrowRightIcon />
              {data.title}
            </Link>
          </div>
          <section className={classNames(styles.container, 'col-12-xs col-9-l')}>
            <h1 data-testid="blogTitle">{data.title}</h1>
            <div className={styles.subHeader}>
              <CalendarIcon /> {data.date}
              {!!data.reading_time && (
                <>
                  | <ClockIcon /> {data.reading_time} minutes
                </>
              )}
            </div>
            {/* Cloudinary removed - using direct image. Previous size: w_{width},h_{height} (dynamic from data.main_image) */}
            <img
              alt={data.title}
              className={styles.featuredImg}
              loading="lazy"
              src={data?.main_image?.url}
              title={data.title}
              width={parseInt(data?.main_image?.width)}
              height={parseInt(data?.main_image?.height)}
            />
            <div className={styles.content}>
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BlogPosting',
                    mainEntityOfPage: {
                      '@type': 'WebPage',
                      '@id': 'https://google.com/article',
                    },
                    headline: data.title,
                    image: images,
                    datePublished: datePublished.toISOString(),
                    dateModified: datePublished.toISOString(),
                    author: {
                      '@type': 'Person',
                      name: config.shopName,
                    },
                    publisher: {
                      '@type': 'Organization',
                      name: config.shopName,
                      logo: {
                        '@type': 'ImageObject',
                        url: config.theme.logos.schema_logo || config.theme.logos.main_logo,
                      },
                    },
                  }),
                }}
              />{' '}
              {blogContent}
            </div>
          </section>
          {(allPostsData || data?.shops) && (
            <div className={classNames(styles.sidebar, 'col-12-xs col-3-l')}>
              <div className={styles.sidebarContent}>
                <h3>
                  <FormattedMessage defaultMessage="Popular posts" id="blog.popularPosts">
                    {(text) => {
                      const parts = text[0].split(' ');
                      return (
                        <>
                          <span>{parts[0]}</span> {parts[1]}
                        </>
                      );
                    }}
                  </FormattedMessage>
                </h3>
                {allPostsData
                  .filter((item) => item.slug !== data.slug)
                  .filter((item) => item.type === 'article')
                  .slice(0, 4)
                  .map((post, index) => {
                    return (
                      <article className={styles.popularPost} key={post.title + index}>
                        <Link className={styles.popularPostLink} to={post.slug}>
                          <div className={styles.imageContainer}>
                            {/* Cloudinary removed - using direct image. Previous size: w_93,h_84 */}
                            <img
                              alt={post.title}
                              loading="lazy"
                              src={post.featured_image}
                              title={post.title}
                            />
                          </div>
                          <div className={styles.popularContent}>
                            <h4>{post.title}</h4>
                            <div className={styles.popularDescription}>
                              {parse(post.introduction, {
                                replace: (domNode) => {
                                  if (domNode.name === 'img' || domNode.name === 'a') {
                                    return <span />;
                                  }
                                },
                              })}
                            </div>
                            <span className={styles.seeMoreLink}>
                              <FormattedMessage id="common.seeMore" defaultMessage="See more" />
                            </span>
                          </div>
                        </Link>
                      </article>
                    );
                  })}
                {!!data.shops?.length && (
                  <>
                    <h3>
                      <FormattedMessage id="shops.relevant" defaultMessage="Relevant shops">
                        {(terms) => {
                          const parts = terms.split(' ');
                          return (
                            <>
                              <span>{parts[0]}</span> {parts[1]}
                            </>
                          );
                        }}
                      </FormattedMessage>
                    </h3>
                    <SimilarShops similarShops={data.shops} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        {!!data.products?.length && <RelatedProductsList productsList={data.products} />}
      </div>
      {allPostsData && (
        <div className={styles.navContainer}>
          <div className="container">
            <div className="row">
              <div className={classNames(styles.navbar, 'col-12-xs')}>
                {previousBlog && (
                  <div className={styles.prev}>
                    <p>
                      <FormattedMessage id="common.previous" defaultMessage="Previous" />
                    </p>
                    <Link onClick={() => scrollToShop('readyForCrawler')} to={previousBlog.slug} data-testid="prevBlog">
                      <ArrowLeftIcon />
                      {previousBlog.title}
                    </Link>
                  </div>
                )}
                {nextBlog && (
                  <div className={styles.next}>
                    <p>
                      <FormattedMessage id="common.next" defaultMessage="Next" />
                    </p>
                    <Link onClick={() => scrollToShop('readyForCrawler')} to={nextBlog.slug} data-testid="nextBlog">
                      {nextBlog.title} <ArrowRightIcon />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <RecentlyViewedProducts />
      <NewsletterSection />
      <Footer />
    </>
  );
};

BlogPage.displayName = 'BlogPage';
BlogPage.propTypes = {
  config: CONFIG_SHAPE,
  staticContext: PropTypes.object.isRequired,
};

export const mapStateToProps = ({ config }) => ({ config });
export default connect(mapStateToProps)(BlogPage);
